/**
 * SQA Report Card — 5-dimension confidence score visualization
 * Variants: full (detail panel), compact (list row), tenant (stripped)
 * Phase 4.7 — WQ-164
 */
import React from 'react';
import type { SQAComponent, SQAAssessment } from '../../services/sqaService';

interface SQAReportCardProps {
  variant: 'full' | 'compact' | 'tenant';
  component: SQAComponent;
  assessment?: SQAAssessment | null;
  openNCRs?: number;
}

const DIMENSIONS = [
  { key: 'test_coverage_score', label: 'Test Coverage', weight: 20 },
  { key: 'conformance_score', label: 'Conformance', weight: 25 },
  { key: 'laws_compliance_score', label: 'Laws Compliance', weight: 20 },
  { key: 'security_score', label: 'Security', weight: 20 },
  { key: 'audit_trail_score', label: 'Audit Trail', weight: 15 },
] as const;

const IL_THRESHOLDS: Record<string, number> = {
  'IL-4': 90, 'IL-3': 85, IL_4: 90, IL_3: 85,
};

const IL_TOOLTIPS: Record<string, string> = {
  'IL-4': 'Critical — Kill switch, Laws framework, secrets management, origin verification. Requires ≥90% confidence.',
  'IL-3': 'High — Multi-tenant isolation, authentication, backup/restore. Requires ≥85% confidence.',
  'IL-2': 'Medium — Business logic, data transformation, API services.',
  'IL-1': 'Low — UI preferences, informational components, cosmetic features.',
  'IL_4': 'Critical — Kill switch, Laws framework, secrets management, origin verification. Requires ≥90% confidence.',
  'IL_3': 'High — Multi-tenant isolation, authentication, backup/restore. Requires ≥85% confidence.',
  'IL_2': 'Medium — Business logic, data transformation, API services.',
  'IL_1': 'Low — UI preferences, informational components, cosmetic features.',
};

function scoreColor(score: number): string {
  if (score >= 80) return 'var(--success, #22c55e)';
  if (score >= 60) return 'var(--warning, #eab308)';
  return 'var(--danger, #ef4444)';
}

function confidenceColor(score: number, il: string): string {
  const threshold = IL_THRESHOLDS[il];
  if (!threshold) return scoreColor(score);
  return score >= threshold ? 'var(--success, #22c55e)' : 'var(--danger, #ef4444)';
}

const SQAReportCard: React.FC<SQAReportCardProps> = ({ variant, component, assessment, openNCRs }) => {
  const confidence = assessment?.confidence_score ?? 0;
  const confidencePct = Math.round(confidence * 100);
  const threshold = IL_THRESHOLDS[component.integrity_level];
  const meetsThreshold = !threshold || confidencePct >= threshold;

  if (variant === 'compact') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          fontSize: 24, fontWeight: 700,
          color: confidenceColor(confidencePct, component.integrity_level),
        }}>
          {assessment ? `${confidencePct}%` : 'N/A'}
        </div>
        <div style={{ flex: 1 }}>
          {assessment && DIMENSIONS.map(d => (
            <div key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
              <div style={{
                width: 60, height: 4, background: 'var(--bg-tertiary, #374151)', borderRadius: 2,
              }}>
                <div style={{
                  width: `${Math.round((assessment[d.key as keyof SQAAssessment] as number) * 100)}%`,
                  height: '100%', borderRadius: 2,
                  background: scoreColor(Math.round((assessment[d.key as keyof SQAAssessment] as number) * 100)),
                }} />
              </div>
            </div>
          ))}
        </div>
        {threshold && (
          <span style={{
            fontSize: 11, padding: '2px 6px', borderRadius: 4,
            background: meetsThreshold ? 'var(--success-bg, #dcfce7)' : 'var(--danger-bg, #fef2f2)',
            color: meetsThreshold ? 'var(--success, #22c55e)' : 'var(--danger, #ef4444)',
          }}>
            {meetsThreshold ? 'PASS' : 'BELOW'}
          </span>
        )}
      </div>
    );
  }

  // Full and tenant variants
  return (
    <div className="glass-card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0 }}>{component.name}</h3>
          <span
            title={IL_TOOLTIPS[component.integrity_level] || component.integrity_level}
            style={{
              fontSize: 12, padding: '2px 8px', borderRadius: 4, cursor: 'help',
              background: component.integrity_level.includes('4') ? '#fef2f2' :
                          component.integrity_level.includes('3') ? '#fff7ed' :
                          component.integrity_level.includes('2') ? '#eff6ff' : '#f3f4f6',
              color: component.integrity_level.includes('4') ? '#dc2626' :
                     component.integrity_level.includes('3') ? '#ea580c' :
                     component.integrity_level.includes('2') ? '#2563eb' : '#6b7280',
            }}>
            {component.integrity_level}
          </span>
        </div>
        <div style={{
          fontSize: 36, fontWeight: 700,
          color: confidenceColor(confidencePct, component.integrity_level),
        }}>
          {assessment ? `${confidencePct}%` : 'N/A'}
        </div>
      </div>

      {threshold && (
        <div style={{
          padding: '6px 12px', borderRadius: 6, marginBottom: 16, fontSize: 13,
          background: meetsThreshold ? 'var(--success-bg, #dcfce7)' : 'var(--danger-bg, #fef2f2)',
          color: meetsThreshold ? 'var(--success, #166534)' : 'var(--danger, #991b1b)',
        }}>
          {meetsThreshold
            ? `Meets ${component.integrity_level} threshold (>= ${threshold}%)`
            : `Below ${component.integrity_level} threshold (requires >= ${threshold}%, current ${confidencePct}%)`
          }
        </div>
      )}

      {assessment && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {DIMENSIONS.map(d => {
            const val = Math.round((assessment[d.key as keyof SQAAssessment] as number) * 100);
            return (
              <div key={d.key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span>{d.label} ({d.weight}%)</span>
                  <span style={{ fontWeight: 600 }}>{val}%</span>
                </div>
                <div style={{
                  width: '100%', height: 8, background: 'var(--bg-tertiary, #374151)',
                  borderRadius: 4, overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${val}%`, height: '100%', borderRadius: 4,
                    background: scoreColor(val),
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!assessment && (
        <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
          No assessment recorded yet.
        </p>
      )}

      {variant === 'full' && assessment && (
        <div style={{ marginTop: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
          <div>Assessed by: {assessment.assessed_by}</div>
          {assessment.wq_id && <div>WQ: {assessment.wq_id}</div>}
          {assessment.notes && <div style={{ marginTop: 8 }}>{assessment.notes}</div>}
          {openNCRs !== undefined && openNCRs > 0 && (
            <div style={{ marginTop: 8, color: 'var(--danger, #ef4444)' }}>
              {openNCRs} open NCR{openNCRs > 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SQAReportCard;
